import { useAtomValue } from 'jotai';
import _ from 'lodash';
import React, { Fragment } from 'react';
import { CardHeader, Row, Col } from 'reactstrap';

import {
  closedVulnMetricsBySeverityCountsAtom,
  openVulnMetricsBySeverityCountsAtom,
  closedVulnMetricsByTimeCountsAtom,
  openVulnMetricsByTimeCountsAtom,
  metricsViewAtom,
  timeViewLabels,
  openVulnMetricsBySeverityAverageDayTotalVulnAtom,
  closeVulnMetricsBySeverityAverageDayTotalVulnAtom,
} from './state';
import BarOrDoughnutChart from '../../components/BarOrDoughnutChart';
import {
  getDrillDownUrl,
  getSeverityFilterValues,
  getSeverityValue,
  getTimeLabelValue,
  mergeArrays,
} from '../../helper';
import { ChartType, Severity, MetricsView } from '../../types';
import { appliedWAIdsAtom, severityFiltersAtom } from '../state';

const Charts = () => {
  const openVulnMetricsBySeverityAverageDayTotalVulnData = useAtomValue(
    openVulnMetricsBySeverityAverageDayTotalVulnAtom,
  );
  const closeVulnMetricsBySeverityAverageDayTotalVulnData = useAtomValue(
    closeVulnMetricsBySeverityAverageDayTotalVulnAtom,
  );
  const openVulnMetricsBySeverityCountsData = useAtomValue(
    openVulnMetricsBySeverityCountsAtom,
  );
  const closedVulnMetricsBySeverityCountsData = useAtomValue(
    closedVulnMetricsBySeverityCountsAtom,
  );

  const openVulnMetricsByTimeCountsData = useAtomValue(
    openVulnMetricsByTimeCountsAtom,
  );
  const closedVulnMetricsByTimeCountsData = useAtomValue(
    closedVulnMetricsByTimeCountsAtom,
  );

  const metricsView = useAtomValue(metricsViewAtom);
  const severityFilters = useAtomValue(severityFiltersAtom);
  const selectedWAIds = useAtomValue(appliedWAIdsAtom);

  function getChartTooltipLabels() {
    const toolTipLabels: string[] = [];
    toolTipLabels.push('Webapps');
    return toolTipLabels;
  }

  function getSeverityChartsDrillDownUrl(
    type: string,
    label: string,
    sourceType: string,
    patchable = false,
    obsolete = false,
  ) {
    return getDrillDownUrl({
      type: type,
      severity: getSeverityValue(Severity[label]).toString(),
      sourceType: 'was_data',
      issueType: 'Vulnerability',
      qid: '',
      patchable: patchable,
      obsolete: obsolete,
      severity_in: undefined,
      hosts_in: selectedWAIds,
    });
  }

  function getTimeChartsDrillDownUrl(
    type: string,
    label: string,
    sourceType: string,
    patchable = false,
    obsolete = false,
  ) {
    const [age_min, age_max] = getTimeLabelValue(label);
    return getDrillDownUrl({
      type: type,
      severity: '',
      sourceType: 'was_data',
      issueType: 'Vulnerability',
      qid: '',
      patchable: patchable,
      obsolete: obsolete,
      severity_in: getSeverityFilterValues(severityFilters),
      hosts_in: selectedWAIds,
      age_min: age_min,
      age_max: age_max,
    });
  }

  function getCalculatedCountsDataset(countsData) {
    // add all data set, if there are multiple datasets
    const clonedDatasets = _.cloneDeep(countsData);
    return clonedDatasets.length > 1
      ? [mergeArrays(clonedDatasets), ...clonedDatasets]
      : clonedDatasets;
  }

  const getChartHeader = (totalCount, title, subTitleCount, subTitle) => {
    return (
      <CardHeader>
        <Row>
          <Col className="d-flex flex-row align-items-baseline">
            <h1 className="d-inline mb-0">{totalCount}</h1>
            <h3 className="card-category px-1">{title}</h3>
          </Col>
          <Col>
            <div className="d-flex flex-row d-inline float-right align-items-baseline">
              <h1 className="d-inline mb-0">{subTitleCount}</h1>
              <h3 className="card-category px-1">{subTitle}</h3>
            </div>
          </Col>
        </Row>
      </CardHeader>
    );
  };

  function getVulnMetricsTooltipFunction(countsDatasets) {
    return function (item, data) {
      const datasetLabel = data.datasets[item.datasetIndex].label;
      if (datasetLabel && countsDatasets) {
        return [
          `Average Days: ${data.datasets[item.datasetIndex].data[
            item.index
          ].toFixed(1)}`,
          `Vulnerabilities Count: ${
            countsDatasets[item.datasetIndex][item.index]
          }`,
        ];
      } else {
        return (
          data.labels[item.index] +
          ': ' +
          data.datasets[item.datasetIndex].data[item.index]
        );
      }
    };
  }

  function getVulnMetricsDoughnutTooltipFunction(countsDatasets) {
    return function (item, data) {
      const datasetLabel = data.datasets[item.datasetIndex].label;
      const legend = data.labels[item.index];
      if (datasetLabel && countsDatasets) {
        return [
          `Vulnerabilities Count: ${
            countsDatasets[item.datasetIndex][item.index]
          }`,
        ];
      } else {
        return (
          data.labels[item.index] +
          ': ' +
          data.datasets[item.datasetIndex].data[item.index]
        );
      }
    };
  }

  function getVulnMetricsDoughnutTooltipTitleFunction() {
    return function (item, data) {
      return data.labels[item[0].index];
    };
  }

  return (
    <Fragment>
      {openVulnMetricsBySeverityAverageDayTotalVulnData &&
        metricsView === MetricsView.Severity && (
          <>
            <div className="col-md-6">
              <BarOrDoughnutChart
                chartType={ChartType.Bar}
                title=""
                labels={['Urgent', 'Critical', 'Serious', 'Medium', 'Minimal']}
                tooltipLabels={getChartTooltipLabels()}
                datasets={
                  openVulnMetricsBySeverityAverageDayTotalVulnData.metricsData
                }
                severityFilters={severityFilters}
                getDrillDownUrl={(label, sourceType) =>
                  getSeverityChartsDrillDownUrl('open', label, sourceType)
                }
                customChartHeader={getChartHeader(
                  openVulnMetricsBySeverityAverageDayTotalVulnData.averageDaysOpenClose,
                  'days (Average Time Open)',
                  openVulnMetricsBySeverityAverageDayTotalVulnData.totalVulnsCount,
                  'Open Vulnerabilities',
                )}
                tooltipsCallbackFunction={getVulnMetricsTooltipFunction(
                  getCalculatedCountsDataset(
                    openVulnMetricsBySeverityCountsData?.metricsData,
                  ),
                )}
                yAxesLabel={'Average Time Open Days'}
                operationType={'Average'}
                countDatasets={openVulnMetricsBySeverityCountsData?.metricsData}
              />
            </div>
          </>
        )}
      {closeVulnMetricsBySeverityAverageDayTotalVulnData &&
        metricsView === MetricsView.Severity && (
          <>
            <div className="col-md-6">
              <BarOrDoughnutChart
                chartType={ChartType.Bar}
                title=""
                labels={['Urgent', 'Critical', 'Serious', 'Medium', 'Minimal']}
                tooltipLabels={getChartTooltipLabels()}
                datasets={
                  closeVulnMetricsBySeverityAverageDayTotalVulnData.metricsData
                }
                severityFilters={severityFilters}
                getDrillDownUrl={(label, sourceType) =>
                  getSeverityChartsDrillDownUrl('closed', label, sourceType)
                }
                customChartHeader={getChartHeader(
                  closeVulnMetricsBySeverityAverageDayTotalVulnData.averageDaysOpenClose,
                  'days (Average Time to Closure)',
                  closeVulnMetricsBySeverityAverageDayTotalVulnData.totalVulnsCount,
                  'Closed Vulnerabilities',
                )}
                tooltipsCallbackFunction={getVulnMetricsTooltipFunction(
                  getCalculatedCountsDataset(
                    closedVulnMetricsBySeverityCountsData?.metricsData,
                  ),
                )}
                yAxesLabel={'Average Time to Closure Days'}
                operationType={'Average'}
                countDatasets={
                  closedVulnMetricsBySeverityCountsData?.metricsData
                }
              />
            </div>
          </>
        )}

      {openVulnMetricsByTimeCountsData && metricsView === MetricsView.Time && (
        <>
          <div className="col-md-6">
            <BarOrDoughnutChart
              chartType={ChartType.Doughnut}
              title=""
              labels={timeViewLabels}
              tooltipLabels={getChartTooltipLabels()}
              datasets={openVulnMetricsByTimeCountsData}
              severityFilters={severityFilters}
              getDrillDownUrl={(label, sourceType) =>
                getTimeChartsDrillDownUrl('open', label, sourceType)
              }
              customChartHeader={getChartHeader(
                openVulnMetricsBySeverityAverageDayTotalVulnData?.averageDaysOpenClose,
                'days (Average Time Open)',
                openVulnMetricsBySeverityAverageDayTotalVulnData?.totalVulnsCount,
                'Open Vulnerabilities',
              )}
              tooltipsCallbackFunction={getVulnMetricsDoughnutTooltipFunction(
                getCalculatedCountsDataset(openVulnMetricsByTimeCountsData),
              )}
              tooltipsTitleCallbackFunction={getVulnMetricsDoughnutTooltipTitleFunction()}
            />
          </div>
        </>
      )}
      {closedVulnMetricsByTimeCountsData &&
        metricsView === MetricsView.Time && (
          <>
            <div className="col-md-6">
              <BarOrDoughnutChart
                chartType={ChartType.Doughnut}
                title=""
                labels={timeViewLabels}
                tooltipLabels={getChartTooltipLabels()}
                datasets={closedVulnMetricsByTimeCountsData}
                severityFilters={severityFilters}
                getDrillDownUrl={(label, sourceType) =>
                  getTimeChartsDrillDownUrl('closed', label, sourceType)
                }
                customChartHeader={getChartHeader(
                  closeVulnMetricsBySeverityAverageDayTotalVulnData?.averageDaysOpenClose,
                  'days (Average Time to Closure)',
                  closeVulnMetricsBySeverityAverageDayTotalVulnData?.totalVulnsCount,
                  'Closed Vulnerabilities',
                )}
                tooltipsCallbackFunction={getVulnMetricsDoughnutTooltipFunction(
                  getCalculatedCountsDataset(closedVulnMetricsByTimeCountsData),
                )}
                tooltipsTitleCallbackFunction={getVulnMetricsDoughnutTooltipTitleFunction()}
              />
            </div>
          </>
        )}
    </Fragment>
  );
};

export default Charts;
